import {
  ArrowDownLeft,
  ArrowUpRight,
  Check,
  CheckCheck,
  FileText,
  LayoutGrid,
  LockKeyhole,
  ShieldCheck,
  Users,
  Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { UsdcIcon } from '@/components/ui/token-icons';

// Fictional example, carried through every screen. Amounts are USDC; the policy
// requires two of three Finance members and caps one account over 24 hours.
const EXAMPLE = {
  amount: '12,500',
  cap: '25,000',
  token: 'USDC',
  network: 'Base',
  recipient: 'Design partner',
  reference: 'Invoice INV-1042',
  requiredApprovals: 2,
  financeMembers: 3,
  windowHours: 24,
};

const SCREENS = [
  'Accounts',
  'Transaction policies',
  'Review payment',
  'Transaction details',
] as const;

function Status({
  children,
  done = false,
}: {
  children: React.ReactNode;
  done?: boolean;
}) {
  return (
    <span className={cn('twf-status', done && 'is-done')}>
      {done && <Check aria-hidden="true" />}
      {children}
    </span>
  );
}

function Field({
  label,
  children,
  highlight = false,
}: {
  label: string;
  children: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div className={cn('twf-field', highlight && 'is-highlighted')}>
      <span>{label}</span>
      <strong>{children}</strong>
    </div>
  );
}

function Accounts({ phase }: { phase: number }) {
  return (
    <>
      <div className="twf-screen-heading">
        <div>
          <span className="twf-overline">Your organization</span>
          <h4>Company accounts</h4>
        </div>
        <span className="twf-faux-action">+ Add account</span>
      </div>
      <div className="twf-account-grid">
        <div className="twf-account">
          <span className="twf-account-icon">
            <Wallet />
          </span>
          <strong>Operating</strong>
          <span>Vendor &amp; payroll payments</span>
          <span className="twf-network">
            <span />
            {EXAMPLE.network}
          </span>
        </div>
        <div className="twf-account">
          <span className="twf-account-icon">
            <LockKeyhole />
          </span>
          <strong>Reserves</strong>
          <span>Long-term company funds</span>
          <span className="twf-network">
            <span />
            {EXAMPLE.network}
          </span>
        </div>
      </div>
      <div
        className={cn('twf-detail-card twf-stage', phase >= 1 && 'is-revealed')}
      >
        <div className="twf-card-heading">
          <Users />
          <strong>Finance</strong>
          <span>{EXAMPLE.financeMembers} members</span>
        </div>
        <div className="twf-people">
          {['Alex', 'Mia', 'Sam'].map((name) => (
            <div key={name}>
              <span className="twf-avatar">{name[0]}</span>
              <span>{name}</span>
              <small>Finance member</small>
            </div>
          ))}
        </div>
      </div>
      <div className={cn('twf-note twf-stage', phase >= 2 && 'is-revealed')}>
        <ShieldCheck />
        <span>
          Changes to members and account access follow your admin approval
          rules.
        </span>
      </div>
      {phase >= 3 && (
        <div className="twf-complete twf-enter">
          <CheckCheck />
          Accounts and team organized
        </div>
      )}
    </>
  );
}

function Policies({ phase }: { phase: number }) {
  return (
    <>
      <div className="twf-screen-heading">
        <div>
          <span className="twf-overline">Payment policy</span>
          <h4>Vendor payments</h4>
        </div>
        <Status done={phase >= 3}>{phase >= 3 ? 'Active' : 'Draft'}</Status>
      </div>
      <div className="twf-detail-card">
        <div className="twf-field-grid">
          <Field label="Source account">Operating</Field>
          <Field label="Token">
            <span className="twf-inline-token">
              <UsdcIcon />
              USDC
            </span>
          </Field>
          <Field label="Recipients">Approved vendors</Field>
          <Field label="Initiators">Finance</Field>
        </div>
      </div>
      <div
        className={cn('twf-detail-card twf-stage', phase >= 1 && 'is-revealed')}
      >
        <div className="twf-card-heading">
          <Users />
          <strong>Approval threshold</strong>
          <span>Manual review</span>
        </div>
        <div className="twf-threshold">
          <strong>
            {EXAMPLE.requiredApprovals} <span>of</span> {EXAMPLE.financeMembers}
          </strong>
          <span>Finance members must sign</span>
        </div>
      </div>
      <div className={cn('twf-limit twf-stage', phase >= 2 && 'is-revealed')}>
        <ShieldCheck />
        <div>
          <strong>
            {EXAMPLE.cap} USDC <span>/ {EXAMPLE.windowHours} hours</span>
          </strong>
          <span>Spending limit · per source account</span>
        </div>
      </div>
      <div className="twf-note">
        <LockKeyhole />
        <span>
          {phase >= 3
            ? 'Policy applied after separate admin approval.'
            : 'Policy changes require separate admin approval.'}
        </span>
        {phase >= 3 && <Check className="twf-note-check twf-enter" />}
      </div>
    </>
  );
}

function Payment({ phase }: { phase: number }) {
  const approvals = Math.min(phase, EXAMPLE.requiredApprovals);
  const executed = phase >= 3;
  return (
    <>
      <div className="twf-screen-heading">
        <div>
          <span className="twf-overline">Operating · {EXAMPLE.network}</span>
          <h4>Vendor payment</h4>
        </div>
        <Status done={executed}>
          {executed
            ? 'Executed'
            : approvals === EXAMPLE.requiredApprovals
              ? 'Ready to execute'
              : 'Pending review'}
        </Status>
      </div>
      <div className="twf-payment-amount">
        <UsdcIcon />
        <strong>
          {EXAMPLE.amount}
          <span>USDC</span>
        </strong>
        <span>to {EXAMPLE.recipient}</span>
      </div>
      <div className="twf-detail-card">
        <Field label="Description">{EXAMPLE.reference}</Field>
        <Field label="Policy">Vendor payments</Field>
      </div>
      <div className="twf-detail-card">
        <div className="twf-card-heading">
          <Users />
          <strong>Finance sign-off</strong>
          <span>
            {approvals} of {EXAMPLE.requiredApprovals} received
          </span>
        </div>
        <div className="twf-signers">
          {['Alex', 'Mia'].map((name, index) => (
            <div key={name} className={cn(approvals > index && 'is-signed')}>
              <span className="twf-avatar">{name[0]}</span>
              <strong>{name}</strong>
              <span>
                {approvals > index ? 'Approved' : 'Awaiting signature'}
              </span>
              {approvals > index ? (
                <Check className="twf-enter" />
              ) : (
                <span className="twf-pending-dot" />
              )}
            </div>
          ))}
        </div>
      </div>
      <div
        className={cn(
          'twf-execute',
          approvals >= EXAMPLE.requiredApprovals && 'is-ready',
          executed && 'is-executed'
        )}
      >
        {executed ? (
          <>
            <CheckCheck />
            Payment executed
          </>
        ) : (
          <>
            <ArrowUpRight />
            {approvals >= EXAMPLE.requiredApprovals
              ? 'Execute payment'
              : 'Waiting for Finance sign-off'}
          </>
        )}
      </div>
    </>
  );
}

function Records({ phase }: { phase: number }) {
  return (
    <>
      <div className="twf-screen-heading">
        <div>
          <span className="twf-overline">Transaction details</span>
          <h4>{EXAMPLE.reference}</h4>
        </div>
        <Status done>Executed</Status>
      </div>
      <div className="twf-record-summary">
        <span className="twf-account-icon">
          <ArrowUpRight />
        </span>
        <div>
          <strong>{EXAMPLE.amount} USDC</strong>
          <span>Operating → {EXAMPLE.recipient}</span>
        </div>
      </div>
      <div
        className={cn('twf-detail-card twf-stage', phase >= 1 && 'is-revealed')}
      >
        <Field label="Network">{EXAMPLE.network}</Field>
        <Field label="Description">{EXAMPLE.reference}</Field>
        <Field label="Transaction hash">0x8a3f…f204</Field>
      </div>
      <div
        className={cn('twf-detail-card twf-stage', phase >= 2 && 'is-revealed')}
      >
        <div className="twf-card-heading">
          <CheckCheck />
          <strong>Approved</strong>
          <span>
            {EXAMPLE.requiredApprovals} of {EXAMPLE.financeMembers} Finance
            members
          </span>
        </div>
        <div className="twf-approval-record">
          <span>
            <Check />
            Alex
          </span>
          <span>
            <Check />
            Mia
          </span>
        </div>
      </div>
      <div className={cn('twf-note twf-stage', phase >= 3 && 'is-revealed')}>
        <FileText />
        <span>
          Transaction and approval audit records
          <br />
          <strong>Available through the audit log API</strong>
        </span>
      </div>
    </>
  );
}

/** Simplified views of the account, policy, and transaction screens; no live forms. */
export function TreasuryWorkflowMockup({
  step,
  phase,
}: {
  step: number;
  phase: number;
}) {
  const Scene = [Accounts, Policies, Payment, Records][step] ?? Accounts;
  const screenName = SCREENS[step] ?? SCREENS[0];
  const descriptions = [
    `Example company accounts: Operating and Reserves on ${EXAMPLE.network}, with ${EXAMPLE.financeMembers} Finance members and separate administrative approval rules.`,
    `Example vendor-payment policy: ${EXAMPLE.requiredApprovals} of ${EXAMPLE.financeMembers} Finance approvals, ${EXAMPLE.token} transfers to approved vendors, and a ${EXAMPLE.cap} ${EXAMPLE.token} limit per source account over ${EXAMPLE.windowHours} hours.`,
    `Example payment of ${EXAMPLE.amount} ${EXAMPLE.token} to ${EXAMPLE.recipient} on ${EXAMPLE.network}, with ${EXAMPLE.requiredApprovals} required Finance approvals before execution.`,
    `Example executed payment record for ${EXAMPLE.reference}: ${EXAMPLE.amount} ${EXAMPLE.token}, the transaction hash, ${EXAMPLE.requiredApprovals} of ${EXAMPLE.financeMembers} Finance approvals, and audit records available through the API.`,
  ];
  return (
    <figure className="twf-figure">
      <figcaption className="sr-only">
        {screenName}. {descriptions[step] ?? descriptions[0]}
      </figcaption>
      <div className="twf-window" aria-hidden="true">
        <div className="twf-window-bar">
          <img src="/den-logo.svg" alt="" />
        </div>
        <div className="twf-app-layout">
          <div className="twf-sidebar">
            {[
              { label: 'Accounts', icon: Wallet, active: step === 0 },
              { label: 'Policies', icon: ShieldCheck, active: step === 1 },
              { label: 'Transactions', icon: ArrowDownLeft, active: step >= 2 },
              { label: 'Members', icon: Users, active: false },
            ].map(({ label, icon: Icon, active }) => (
              <span key={label} className={cn(active && 'is-current')}>
                <Icon />
                {label}
              </span>
            ))}
            <span className="twf-sidebar-bottom">
              <LayoutGrid />
              Workspace
            </span>
          </div>
          <div key={step} className="twf-screen twf-enter">
            <Scene phase={phase} />
          </div>
        </div>
      </div>
    </figure>
  );
}
